// Mock data for trash and recycle bins based on real bin locations in Cary
// Fill levels are determined by location type and crowd movement patterns

// Location types and their typical crowd patterns
const LOCATION_TYPES = {
  PARK_COURTS: { // Sports areas - high activity during evenings/weekends
    baseTraffic: 0.7,
    peakHours: [17, 18, 19, 20], // 5-8 PM
    weekendMultiplier: 1.4
  },
  ACADEMY_PLAZA: { // Commercial/shopping area - high traffic during day
    baseTraffic: 0.8,
    peakHours: [11, 12, 13, 17, 18], // Lunch and evening rush
    weekendMultiplier: 1.2
  },
  GREAT_LAWN: { // Open park space - moderate consistent traffic
    baseTraffic: 0.5,
    peakHours: [12, 13, 17, 18], // Lunch and evening
    weekendMultiplier: 1.6
  },
  BARKYARD: { // Dog park - high activity mornings and evenings
    baseTraffic: 0.6,
    peakHours: [7, 8, 17, 18, 19], // Morning and evening dog walks
    weekendMultiplier: 1.8
  },
  FRANTZ_SQUARE: { // Event/gathering space - variable traffic
    baseTraffic: 0.4,
    peakHours: [12, 13, 18, 19], // Lunch and evening events
    weekendMultiplier: 1.3
  },
  THE_NEST: { // Community center - steady moderate traffic
    baseTraffic: 0.6,
    peakHours: [9, 12, 15, 18], // Program times
    weekendMultiplier: 1.1
  },
  GATHERING_GARDENS: { // Garden/nature area - lower traffic
    baseTraffic: 0.3,
    peakHours: [10, 11, 16, 17], // Pleasant weather times
    weekendMultiplier: 1.5
  },
  SKYWALK: { // Transit/walkway - consistent moderate traffic
    baseTraffic: 0.5,
    peakHours: [8, 12, 17], // Commute and lunch times
    weekendMultiplier: 0.8
  }
};

// Helper function to determine location type from bin name
const getLocationType = (binName) => {
  const name = binName.toLowerCase();
  if (name.includes('park street courts')) return LOCATION_TYPES.PARK_COURTS;
  if (name.includes('academy plaza')) return LOCATION_TYPES.ACADEMY_PLAZA;
  if (name.includes('great lawn')) return LOCATION_TYPES.GREAT_LAWN;
  if (name.includes('barkyard')) return LOCATION_TYPES.BARKYARD;
  if (name.includes('frantz square')) return LOCATION_TYPES.FRANTZ_SQUARE;
  if (name.includes('the nest')) return LOCATION_TYPES.THE_NEST;
  if (name.includes('gathering gardens')) return LOCATION_TYPES.GATHERING_GARDENS;
  if (name.includes('skywalk')) return LOCATION_TYPES.SKYWALK;
  return LOCATION_TYPES.GREAT_LAWN; // Default
};

// Helper function to calculate fill level based on time and location
const calculateFillLevel = (locationType, dayOffset = 0, hourOffset = 0, isWeekend = false, seed = 0) => {
  const currentHour = (new Date().getHours() + hourOffset) % 24;
  const daysSinceLastEmpty = Math.abs(dayOffset);
  
  let fillLevel = locationType.baseTraffic * 30; // Base fill level
  
  // Add peak hour bonus
  if (locationType.peakHours.includes(currentHour)) {
    fillLevel += 20;
  }
  
  // Weekend multiplier
  if (isWeekend) {
    fillLevel *= locationType.weekendMultiplier;
  }
  
  // Accumulate over days (bins get fuller over time)
  fillLevel += daysSinceLastEmpty * 15;
  
  // Add deterministic "randomness" based on seed
  const pseudoRandom = (Math.sin(seed * 9999) + 1) / 2; // 0-1 range
  fillLevel += (pseudoRandom - 0.5) * 20;
  
  // Ensure within bounds
  return Math.max(5, Math.min(95, Math.round(fillLevel)));
};

// Helper function to generate time series data
const generateTimeSeries = (locationType, binType, seed = 0) => {
  const timeSeries = [];
  const now = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    let fillLevel;
    if (i === 3) {
      // Bin was emptied 3 days ago
      const pseudoRandom = (Math.sin((seed + i) * 7777) + 1) / 2;
      fillLevel = Math.floor(pseudoRandom * 15) + 5; // 5-20%
    } else if (i > 3) {
      // Before emptying - higher levels
      fillLevel = calculateFillLevel(locationType, -(i-3), 0, isWeekend, seed + i);
    } else {
      // After emptying - accumulating
      fillLevel = calculateFillLevel(locationType, i-3, 0, isWeekend, seed + i);
    }
    
    timeSeries.push({
      time: i === 0 ? 'Today' : `Day -${i}`,
      fillPercent: fillLevel,
      timestamp: date.toISOString()
    });
  }
  
  return timeSeries;
};

// Generate mock data for all bins from the mongosh file
export const generateBinMockData = () => {
  const binDefinitions = [
    { name: "Park Street Courts Recycle Bin 4", mac: "c6:32:47:23:c9:d6", lat: 35.7934806, lng: -78.7800037 },
    { name: "Academy Plaza Recycle Bin 4", mac: "f6:6e:23:49:ef:35", lat: 35.792, lng: -78.7812939 },
    { name: "Great Lawn Recycle Bin 1", mac: "f6:6e:23:49:ef:35", lat: 35.7924054, lng: -78.7806384 },
    { name: "Barkyard Trash Can 2", mac: "46:ed:59:72:e6:94", lat: 35.7929044, lng: -78.7790277 },
    { name: "Great Lawn Trash Can 1", mac: "b2:f2:5e:c7:58:48", lat: 35.7923953, lng: -78.7806382 },
    { name: "Barkyard Trash Can 5", mac: "a2:51:c8:8b:2a:d3", lat: 35.7925287, lng: -78.7789351 },
    { name: "Frantz Square Recycle Bin 4", mac: "a2:51:c8:8b:2a:d3", lat: 35.79197, lng: -78.7812371 },
    { name: "Frantz Square Recycle Bin 1", mac: "62:c8:19:30:10:aa", lat: 35.7917247, lng: -78.7808565 },
    { name: "Academy Plaza Trash Can 1", mac: "62:c8:19:30:10:aa", lat: 35.7921645, lng: -78.7808008 },
    { name: "Academy Plaza Trash Can 2", mac: "22:2e:18:b2:8c:e2", lat: 35.7920294, lng: -78.7811908 },
    { name: "Barkyard Recycle Bin 1", mac: "22:2e:18:b2:8c:e2", lat: 35.7930612, lng: -78.779233 },
    { name: "Great Lawn Trash Can 3", mac: "4a:7c:e7:69:9e:cf", lat: 35.7923997, lng: -78.7799947 },
    { name: "Great Lawn Recycle Bin 4", mac: "c6:38:78:b6:b5:dd", lat: 35.7926875, lng: -78.7802444 },
    { name: "The Nest Recycle Bin 4", mac: "a6:84:9c:55:ee:35", lat: 35.7922988, lng: -78.7800419 },
    { name: "Gathering Gardens Recycle Bin 2", mac: "a2:86:7b:07:ae:80", lat: 35.7912367, lng: -78.780242 },
    { name: "Barkyard Recycle Bin 4", mac: "a2:86:7b:07:ae:80", lat: 35.7928783, lng: -78.7789374 },
    { name: "Park Street Courts Trash Can 3", mac: "bc:d0:74:6d:a1:02", lat: 35.7934059, lng: -78.7796852 },
    { name: "Academy Plaza Recycle Bin 1", mac: "bc:d0:74:6d:a1:02", lat: 35.792172, lng: -78.780805 },
    { name: "Frantz Square Trash Can 3", mac: "bc:d0:74:6d:a1:02", lat: 35.7918329, lng: -78.7812988 },
    { name: "The Nest Trash Can 1", mac: "d6:4f:81:f0:64:c7", lat: 35.7921089, lng: -78.7800105 },
    { name: "Gathering Gardens Recycle Bin 1", mac: "9a:01:82:4c:9a:fe", lat: 35.7929183, lng: -78.779685 },
    { name: "Great Lawn Recycle Bin 2", mac: "f8:54:f6:96:4e:97", lat: 35.7925396, lng: -78.7805492 },
    { name: "Academy Plaza Recycle Bin 5", mac: "f8:54:f6:96:4e:97", lat: 35.7924126, lng: -78.781353 },
    { name: "The Nest Recycle Bin 1", mac: "f8:54:f6:96:4e:97", lat: 35.7921017, lng: -78.780016 },
    { name: "Frantz Square Trash Can 2", mac: "34:2e:b7:55:49:e8", lat: 35.7916552, lng: -78.7811438 },
    { name: "Barkyard Trash Can 1", mac: "5e:95:f7:48:aa:d9", lat: 35.7930548, lng: -78.7792231 },
    { name: "Barkyard Trash Can 4", mac: "5e:95:f7:48:aa:d9", lat: 35.7928896, lng: -78.7789435 },
    { name: "Park Street Courts Recycle Bin 2", mac: "ca:bb:94:0e:06:3f", lat: 35.7932486, lng: -78.779248 },
    { name: "Skywalk Trash Can", mac: "ca:bb:94:0e:06:3f", lat: 35.7920026, lng: -78.7788543 },
    { name: "The Nest Recycle Bin 2", mac: "42:41:55:c3:67:b4", lat: 35.7920409, lng: -78.7804649 },
    { name: "Park Street Courts Trash Can 2", mac: "66:0b:29:9e:d0:0a", lat: 35.7932584, lng: -78.779244 },
    { name: "Academy Plaza Recycle Bin 3", mac: "16:77:e9:74:05:c0", lat: 35.7924407, lng: -78.7809988 },
    { name: "Great Lawn Trash Can 4", mac: "4e:a0:89:a2:81:21", lat: 35.7926811, lng: -78.780239 },
    { name: "Frantz Square Recycle Bin 2", mac: "34:8c:5e:9d:6c:2d", lat: 35.7916469, lng: -78.7811402 },
    { name: "Gathering Gardens Trash Can 1", mac: "34:8c:5e:9d:6c:2d", lat: 35.79292, lng: -78.7796759 },
    { name: "Skywalk Recycle Bin", mac: "34:8c:5e:9d:6c:2d", lat: 35.7919922, lng: -78.7788535 },
    { name: "Barkyard Recycle Bin 3", mac: "04:c1:03:14:56:33", lat: 35.7930533, lng: -78.7789585 },
    { name: "The Nest Trash Can 3", mac: "58:6c:25:e9:f7:d7", lat: 35.7922684, lng: -78.7802104 },
    { name: "The Nest Recycle Bin 3", mac: "58:6c:25:e9:f7:d7", lat: 35.7922703, lng: -78.7802014 },
    { name: "The Nest Trash Can 2", mac: "58:6c:25:e9:f7:d7", lat: 35.7920392, lng: -78.7804558 },
    { name: "The Nest Trash Can 4", mac: "c2:d0:b8:1d:4a:d8", lat: 35.7923053, lng: -78.7800493 },
    { name: "Academy Plaza Trash Can 3", mac: "c2:d0:b8:1d:4a:d8", lat: 35.7924385, lng: -78.7810099 },
    { name: "Academy Plaza Trash Can 5", mac: "c4:03:a8:ce:d5:b5", lat: 35.7924191, lng: -78.7813645 },
    { name: "Frantz Square Trash Can 1", mac: "c4:03:a8:ce:d5:b5", lat: 35.7917326, lng: -78.7808472 },
    { name: "Gathering Gardens Trash Can 2", mac: "42:cd:b7:9b:b9:5c", lat: 35.7912186, lng: -78.780242 },
    { name: "Park Street Courts Trash Can 4", mac: "42:cd:b7:9b:b9:5c", lat: 35.7934809, lng: -78.7800168 },
    { name: "Barkyard Recycle Bin 2", mac: "4a:cb:c6:f0:d4:dc", lat: 35.7928986, lng: -78.7790365 },
    { name: "Academy Plaza Trash Can 4", mac: "aa:eb:dc:07:bb:56", lat: 35.7919923, lng: -78.7812858 },
    { name: "Barkyard Trash Can 3", mac: "f0:79:60:26:5a:00", lat: 35.7930635, lng: -78.7789623 },
    { name: "Park Street Courts Trash Can 1", mac: "f0:79:60:26:5a:00", lat: 35.7934352, lng: -78.7790588 },
    { name: "Park Street Courts Recycle Bin 3", mac: "ce:db:11:5f:0b:49", lat: 35.7933983, lng: -78.7796738 },
    { name: "Academy Plaza Recycle Bin 2", mac: "ce:db:11:5f:0b:49", lat: 35.7920301, lng: -78.7811775 },
    { name: "Frantz Square Recycle Bin 3", mac: "a2:ef:08:03:d1:f4", lat: 35.7918304, lng: -78.7813077 },
    { name: "Great Lawn Recycle Bin 3", mac: "a6:84:9c:55:ee:35", lat: 35.7924014, lng: -78.7799829 },
    { name: "Great Lawn Trash Can 2", mac: "12:e5:c4:3c:4e:96", lat: 35.7925394, lng: -78.7805364 },
    { name: "Park Street Courts Recycle Bin 1", mac: "0e:8e:70:e8:40:82", lat: 35.7934387, lng: -78.7790538 }
  ];

  const bins = binDefinitions.map((binDef, index) => {
    const binType = binDef.name.toLowerCase().includes('recycle') ? 'Recycle' : 'Trash';
    const locationType = getLocationType(binDef.name);
    const seed = index * 123; // Use index as seed for consistent data
    const currentFillLevel = calculateFillLevel(locationType, 0, 0, false, seed);
    
    // Generate cleaning schedule based on fill level and location type (deterministic)
    const pseudoRandom1 = (Math.sin(seed * 1111) + 1) / 2;
    const pseudoRandom2 = (Math.sin(seed * 2222) + 1) / 2;
    const pseudoRandom3 = (Math.sin(seed * 3333) + 1) / 2;
    const pseudoRandom4 = (Math.sin(seed * 4444) + 1) / 2;
    const pseudoRandom5 = (Math.sin(seed * 5555) + 1) / 2;
    
    const lastClearedHours = Math.floor(pseudoRandom1 * 48) + 12; // 12-60 hours ago
    const lastClearedAt = new Date(Date.now() - lastClearedHours * 60 * 60 * 1000);
    
    // Next cleaning based on current fill level and location traffic
    const hoursUntilNext = currentFillLevel > 80 ? Math.floor(pseudoRandom2 * 4) + 1 : 
                          currentFillLevel > 60 ? Math.floor(pseudoRandom2 * 12) + 4 :
                          Math.floor(pseudoRandom2 * 24) + 8;
    const nextCleaningAt = new Date(Date.now() + hoursUntilNext * 60 * 60 * 1000);
    
    // Determine status
    let status = 'OK';
    if (currentFillLevel > 85) status = 'Needs Attention';
    else if (currentFillLevel > 95) status = 'Critical';
    else if (lastClearedHours > 72) status = 'Overdue';
    
    return {
      id: `BIN-${String(index + 1).padStart(3, '0')}`,
      name: binDef.name,
      type: binType,
      location: binDef.name.split(' ').slice(0, 2).join(' '), // First two words as location
      macAddress: binDef.mac,
      coordinates: {
        latitude: binDef.lat,
        longitude: binDef.lng
      },
      lastClearedAt: lastClearedAt.toISOString(),
      nextCleaningAt: nextCleaningAt.toISOString(),
      fillPercent: currentFillLevel,
      status: status,
      locationType: Object.keys(LOCATION_TYPES).find(key => LOCATION_TYPES[key] === locationType),
      timeSeries7d: generateTimeSeries(locationType, binType, seed),
      // Additional sensor data
      sensorData: {
        temperature: Math.floor(pseudoRandom3 * 15) + 15, // 15-30°C
        humidity: Math.floor(pseudoRandom4 * 30) + 40, // 40-70%
        lastPing: new Date(Date.now() - Math.floor(pseudoRandom5 * 300000)).toISOString(), // Last 5 minutes
        batteryLevel: Math.floor(pseudoRandom3 * 30) + 70, // 70-100%
        signalStrength: Math.floor(pseudoRandom4 * 40) - 70 // -70 to -30 dBm
      }
    };
  });

  return {
    bins: bins,
    summary: {
      totalBins: bins.length,
      trashBins: bins.filter(b => b.type === 'Trash').length,
      recycleBins: bins.filter(b => b.type === 'Recycle').length,
      needsAttention: bins.filter(b => b.status === 'Needs Attention' || b.status === 'Critical').length,
      averageFillLevel: Math.round(bins.reduce((sum, b) => sum + b.fillPercent, 0) / bins.length),
      lastUpdated: new Date().toISOString()
    }
  };
};

// Get bin data by MAC address (for entity selection)
export const getBinByMacAddress = (macAddress) => {
  const allData = generateBinMockData();
  return allData.bins.find(bin => bin.macAddress === macAddress);
};

// Get bins by location type
export const getBinsByLocation = (locationType) => {
  const allData = generateBinMockData();
  return allData.bins.filter(bin => bin.locationType === locationType);
};

// Get bins needing attention
export const getBinsNeedingAttention = () => {
  const allData = generateBinMockData();
  return allData.bins.filter(bin => 
    bin.status === 'Needs Attention' || 
    bin.status === 'Critical' || 
    bin.status === 'Overdue'
  );
};

// Export default data for immediate use
export default generateBinMockData();
